import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Tag, CheckCircle2, Loader2, Info, ImagePlus, X, Mail as MailIcon } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import userApi from "@/lib/userApi";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { LOCATIONS } from "@/lib/categories";
import { useSettings } from "@/context/SettingsContext";
import { useAppAuth } from "@/context/AppAuthContext";

const EMPTY = {
  title: "",
  listing_type: "",
  description: "",
  price: "",
  location: LOCATIONS[0],
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  contact_social: "",
  images: [],
};

export default function SubmitListing() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [resending, setResending] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const settings = useSettings();
  const { user, loading } = useAppAuth();

  // Only registered users may submit a listing.
  useEffect(() => {
    if (!loading && !user) {
      navigate("/vhod", { state: { from: "/publikuvai-obyava" } });
    }
  }, [loading, user, navigate]);

  // Prefill contact name/email from the logged-in profile (editable).
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        contact_name: f.contact_name || user.name || "",
        contact_email: f.contact_email || user.email || "",
        contact_phone: f.contact_phone || user.phone || "",
      }));
    }
  }, [user]);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const resolveImg = (src) => (src && src.startsWith("/") ? `${backend}${src}` : src);

  const { data: types } = useQuery({
    queryKey: ["listing-types"],
    queryFn: () => userApi.get("/listing-types").then((r) => r.data),
  });
  const typeList = types || [];

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    if ((form.images || []).length + files.length > 10) {
      toast.error("Максимум 10 снимки на обява.");
      return;
    }
    setUploading(true);
    let added = 0;
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error(`„${file.name}“ не е изображение — пропуснато.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`„${file.name}“ е над 5 MB — пропуснато.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await userApi.post("/listings/upload", fd);
        setForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
        added += 1;
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    }
    setUploading(false);
    if (added) toast.success(`Добавени ${added} снимки.`);
  };

  const removeImage = (idx) =>
    setForm((f) => ({ ...f, images: (f.images || []).filter((_, i) => i !== idx) }));

  const resendVerification = async () => {
    setResending(true);
    try {
      const { data } = await userApi.post("/account/resend-verification");
      toast.success(data.message || "Изпратихме нова връзка.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.listing_type || !form.description.trim() || !form.location || !form.contact_name.trim()) {
      toast.error("Попълнете заглавие, вид, описание, локация и име за контакт.");
      return;
    }
    setSubmitting(true);
    try {
      await userApi.post("/listings", form);
      setDone(true);
      toast.success("Обявата е изпратена за преглед!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Layout showTicker={false}>
        <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="listing-success">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-9 w-9 text-green-600" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-extrabold text-slate-900">Благодарим Ви!</h1>
          <p className="mt-3 text-slate-600">
            Обявата беше изпратена до редакцията. След кратък преглед ще бъде публикувана
            <b> напълно безплатно</b> в рубрика „Обяви“.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <button
              onClick={() => { setForm(EMPTY); setDone(false); }}
              data-testid="listing-new-btn"
              className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary"
            >
              Нова обява
            </button>
            <button
              onClick={() => navigate("/obyavi")}
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0041A8]"
            >
              Виж обявите
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20";
  const labelClass = "mb-1.5 block text-sm font-semibold text-slate-700";

  if (user && !user.email_verified) {
    return (
      <Layout showTicker={false}>
        <Seo title="Потвърдете имейла си" noindex />
        <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid="listing-verify-gate">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <Info className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="mt-6 font-display text-2xl font-extrabold text-slate-900">Потвърдете имейла си</h1>
          <p className="mt-3 text-slate-600">
            За да публикувате обява, първо трябва да потвърдите имейл адреса <b>{user.email}</b>.
            Проверете пощата си (и папка „Спам") за връзката за потвърждение.
          </p>
          <button
            onClick={resendVerification}
            disabled={resending}
            data-testid="resend-verification-btn"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0041A8] disabled:opacity-60"
          >
            {resending ? <Loader2 className="h-5 w-5 animate-spin" /> : <MailIcon className="h-5 w-5" />}
            Изпрати връзката отново
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showTicker={false}>
      <Seo
        title="Публикувай безплатна обява"
        description="Публикувайте безплатна обява за квартал Подуяне — продажби, услуги, имоти, работа и още. Прегледано от редактор преди публикуване."
      />
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
            Безплатни обяви
          </span>
          <h1 className="mt-4 font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">
            Публикувай обява
          </h1>
          <p className="mx-auto mt-3 max-w-md text-slate-600">
            Продавате, търсите или предлагате услуга в квартала? Публикувайте безплатна обява.
          </p>
        </div>

        <div className="mt-6 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Обявите се преглеждат от редакцията преди публикуване. Публикуването е безплатно. Обявата е валидна {settings.listing_valid_days || 30} дни след одобрение.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="listing-form">
          <div>
            <label className={labelClass}>Заглавие *</label>
            <input className={inputClass} data-testid="listing-title" value={form.title} onChange={set("title")} placeholder="напр. Продавам детско колело" />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Вид обява *</label>
              <select className={inputClass} data-testid="listing-type" value={form.listing_type} onChange={set("listing_type")}>
                <option value="">— изберете —</option>
                {typeList.map((t) => (
                  <option key={t.id || t.slug} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Цена в € (по избор)</label>
              <input className={inputClass} data-testid="listing-price" value={form.price} onChange={set("price")} placeholder="напр. 180 € / по договаряне" />
            </div>
          </div>

          <div>
            <label className={labelClass}>Локация *</label>
            <select className={inputClass} data-testid="listing-location" value={form.location} onChange={set("location")}>
              {LOCATIONS.map((l) => (<option key={l} value={l}>{l}</option>))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Описание *</label>
            <textarea rows={5} className={inputClass} data-testid="listing-description" value={form.description} onChange={set("description")} placeholder="Опишете подробно какво предлагате или търсите." />
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className={labelClass + " mb-0"}>Снимки (по избор)</span>
                <p className="text-xs text-slate-400">До 10 снимки, всяка до 5 MB.</p>
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="listing-images-file" onChange={onImages} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="listing-images-add"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {uploading ? "Качване…" : "Добави снимки"}
              </button>
            </div>
            {(form.images || []).length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                {form.images.map((src, i) => (
                  <div key={i} className="relative aspect-square overflow-hidden rounded-lg bg-slate-100" data-testid={`listing-image-${i}`}>
                    <img src={resolveImg(src)} alt="" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      data-testid={`listing-image-remove-${i}`}
                      aria-label="Премахни"
                      className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <h3 className="mb-3 font-display text-sm font-bold text-slate-900">Данни за контакт</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Име *</label>
                <input className={inputClass} data-testid="listing-name" value={form.contact_name} onChange={set("contact_name")} placeholder="Име за контакт" />
              </div>
              <div>
                <label className={labelClass}>Телефон</label>
                <input className={inputClass} data-testid="listing-phone" value={form.contact_phone} onChange={set("contact_phone")} placeholder="напр. 0888…" />
              </div>
              <div>
                <label className={labelClass}>Имейл</label>
                <input type="email" className={inputClass} data-testid="listing-email" value={form.contact_email} onChange={set("contact_email")} placeholder="незадължително" />
              </div>
              <div>
                <label className={labelClass}>Линк към соц. мрежа</label>
                <input className={inputClass} data-testid="listing-social" value={form.contact_social} onChange={set("contact_social")} placeholder="Facebook / Instagram профил (незадължително)" />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || uploading}
            data-testid="listing-submit-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0041A8] disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Tag className="h-5 w-5" />}
            Изпрати за преглед
          </button>
        </form>
      </div>
    </Layout>
  );
}
